import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { siRatingApis } from "../../../api/siRatingApis";
import { gradeColors } from "../../sustainabilityIndex/gradeColors";

const SOURCE_BADGE = {
  sdm: { label: "SDM", cls: "sdm" },
  derived: { label: "Derived", cls: "derived" },
  pdf: { label: "PDF", cls: "pdf" },
  override: { label: "Override", cls: "override" },
};

const STATUS_LABEL = { scored: "Scored", missing: "Not reported", no_benchmark: "Not benchmarked" };

function fmt(v, dp = 3) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  const n = Number(v);
  const abs = Math.abs(n);
  return n.toLocaleString("en-US", { maximumFractionDigits: abs >= 1000 ? 0 : abs >= 1 ? 2 : dp });
}

function benchmarkText(k) {
  if (k.type === 3) return "Yes = 1";
  if (k.type === 4) return "Zero = 1";
  if (k.lb == null || k.ub == null) return "—";
  return k.type === 5 ? `${fmt(k.lb)} – ${fmt(k.ub)} (opt ${fmt(k.mb)})` : `${fmt(k.lb)} – ${fmt(k.ub)}`;
}

const gradeColor = (g) => gradeColors[String(g || "F").charAt(0)] || "#ED4A3C";

function errorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function SiRatingResults({ rating, onChange }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sectionTab, setSectionTab] = useState("all");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [editKpi, setEditKpi] = useState(null);
  const [overrideValue, setOverrideValue] = useState("");
  const [overrideNote, setOverrideNote] = useState("");
  const [uploadRole, setUploadRole] = useState("gap");
  const fileInput = useRef(null);

  const result = rating?.result;
  const finalized = rating?.status === "finalized";
  const sections = result?.sections || [];
  const kpis = useMemo(
    () => (result?.kpis || []).filter((k) => sectionTab === "all" || k.section === sectionTab),
    [result, sectionTab]
  );
  const pendingCandidates = (rating?.pdfCandidates || []).filter((c) => c.status === "pending");
  const reviewedCandidates = (rating?.pdfCandidates || []).filter((c) => c.status !== "pending");
  const linkedReportId = searchParams.get("fromReport") || rating?.report || null;

  const act = async (key, fn) => {
    setBusy(key);
    setError("");
    try {
      const updated = await fn();
      if (updated) onChange?.(updated);
    } catch (err) {
      setError(errorMessage(err, "Action failed"));
    } finally {
      setBusy("");
    }
  };

  const reviewCandidate = (c, status, role) =>
    act(`cand-${c._id}`, () =>
      siRatingApis.updateValues(rating._id, { candidates: [{ id: c._id, status, ...(role ? { role } : {}) }] })
    );

  const saveOverride = (value) =>
    act("override", async () => {
      const updated = await siRatingApis.updateValues(rating._id, {
        overrides: { [editKpi.datapoint]: value === undefined ? null : { value, note: overrideNote } },
      });
      setEditKpi(null);
      return updated;
    });

  if (!result) return null;
  const ref = rating.references || {};

  return (
    <div className="si-rating-results">
      <div className="si-rating-card si-rating-hero">
        <div className="si-rating-hero__main">
          <p className="si-rating-eyebrow">
            SI Rating · {rating.ratingSector} · {rating.sdm?.reportingPeriod || "Annual"} {rating.year || ""}
            <span className={`si-rating-status si-rating-status--${rating.status}`}>
              {finalized ? "Final" : "Draft"}
            </span>
          </p>
          <h2>{rating.companyName || rating.sdm?.projectName || "Company"}</h2>
          <p className="si-rating-muted">
            {result.overall.reportedCount} of {result.overall.kpiCount} KPIs reported ·{" "}
            {Math.round((result.overall.coverage || 0) * 100)}% weighted coverage · catalogue {rating.catalogVersion}
          </p>
          <div className="si-rating-actions">
            <Button
              size="small"
              variant="outlined"
              startIcon={busy === "xlsx" ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />}
              onClick={() => act("xlsx", () => siRatingApis.downloadExcel(rating._id))}
              disabled={!!busy}
            >
              Scoring Excel
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={busy === "pdf" ? <CircularProgress size={14} /> : <PictureAsPdfOutlinedIcon />}
              onClick={() => act("pdf", () => siRatingApis.downloadReportPdf(rating._id))}
              disabled={!!busy}
            >
              Rating Report PDF
            </Button>
            {!finalized && (
              <>
                {rating?.sdm?.projectId && (
                  <Button
                    size="small"
                    startIcon={busy === "refresh" ? <CircularProgress size={14} /> : <RefreshRoundedIcon />}
                    onClick={() => act("refresh", () => siRatingApis.refreshSdm(rating._id))}
                    disabled={!!busy}
                  >
                    Re-pull SDM
                  </Button>
                )}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={busy === "finalize" ? <CircularProgress size={14} color="inherit" /> : <TaskAltRoundedIcon />}
                  onClick={() => act("finalize", () => siRatingApis.finalize(rating._id))}
                  disabled={!!busy || pendingCandidates.length > 0}
                  sx={{ bgcolor: "#02b880", "&:hover": { bgcolor: "#029a6c" } }}
                >
                  Finalize
                </Button>
              </>
            )}
            {linkedReportId && (
              <Button
                size="small"
                variant="contained"
                color="inherit"
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={() =>
                  navigate(`/add-data-points/${linkedReportId}`, {
                    state: { generationMethod: "ai", siRatingId: rating._id },
                  })
                }
                sx={{ bgcolor: "#005468", color: "#fff", "&:hover": { bgcolor: "#003d4d" } }}
              >
                Continue to Add Data Points
              </Button>
            )}
          </div>
          {pendingCandidates.length > 0 && !finalized && (
            <p className="si-rating-hint">Review the {pendingCandidates.length} extracted PDF value(s) below before continuing.</p>
          )}
          {linkedReportId && (
            <p className="si-rating-hint">
              Accepted PDF values sync into this report&apos;s KPI forms. Continue anytime to review or complete data entry.
            </p>
          )}
          {error && <p className="si-rating-error">{error}</p>}
        </div>
        <div className="si-rating-hero__grade" style={{ borderColor: gradeColor(result.overall.grade) }}>
          <span className="si-rating-hero__grade-value" style={{ color: gradeColor(result.overall.grade) }}>
            {result.overall.grade}
          </span>
          <span className="si-rating-hero__score">{fmt(result.overall.score)} / 100</span>
        </div>
      </div>

      <div className="si-rating-sections">
        {sections.map((s) => {
          const avg = ref.industryAverage?.sections?.find((x) => x.key === s.key)?.scorePct;
          const prev = rating.previousResult?.sections?.find((x) => x.key === s.key)?.scorePct;
          return (
            <div key={s.key} className="si-rating-card si-rating-section">
              <span className="si-rating-section__label">{s.label}</span>
              <span className="si-rating-section__score">
                {fmt(s.scorePct)} <b style={{ color: gradeColor(s.grade) }}>{s.grade}</b>
              </span>
              <div className="si-rating-bar">
                <span style={{ width: `${Math.min(100, s.scorePct)}%` }} />
                {avg != null && <i style={{ left: `${Math.min(100, avg)}%` }} title={`Industry average ${fmt(avg)}`} />}
              </div>
              <span className="si-rating-muted">
                {s.reportedCount}/{s.kpiCount} KPIs · {Math.round((s.coverage || 0) * 100)}% coverage
                {avg != null ? ` · avg ${fmt(avg)}` : ""}
                {prev != null ? ` · prev ${fmt(prev)}` : ""}
              </span>
            </div>
          );
        })}
      </div>

      {(ref.industryAverage || rating.previousResult) && (
        <div className="si-rating-card si-rating-refs">
          <div>
            <span className="si-rating-muted">Industry average</span>
            <b>
              {fmt(ref.industryAverage?.overall?.score)} {ref.industryAverage?.overall?.grade}
            </b>
          </div>
          <div>
            <span className="si-rating-muted">Industry leaders</span>
            <b>
              {fmt(ref.industryLeaders?.overall?.score)} {ref.industryLeaders?.overall?.grade}
            </b>
          </div>
          <div>
            <span className="si-rating-muted">Previous assessment</span>
            <b>
              {rating.previousResult
                ? `${fmt(rating.previousResult.overall.score)} ${rating.previousResult.overall.grade}`
                : "—"}
            </b>
          </div>
          <div>
            <span className="si-rating-muted">Change</span>
            <b className={rating.comparison?.overall?.delta >= 0 ? "si-up" : "si-down"}>
              {rating.comparison?.overall?.delta != null
                ? `${rating.comparison.overall.delta >= 0 ? "+" : ""}${fmt(rating.comparison.overall.delta)}`
                : "—"}
            </b>
          </div>
        </div>
      )}

      <div className="si-rating-card">
        <div className="si-rating-card__head">
          <h3>Previous reports (AI extraction)</h3>
          {!finalized && (
            <div className="si-rating-form-row" style={{ margin: 0 }}>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel id="si-upload-role">Use values to</InputLabel>
                <Select labelId="si-upload-role" label="Use values to" value={uploadRole} onChange={(e) => setUploadRole(e.target.value)}>
                  <MenuItem value="gap">Fill gaps in this year</MenuItem>
                  <MenuItem value="baseline">Previous-year baseline</MenuItem>
                </Select>
              </FormControl>
              <input
                ref={fileInput}
                type="file"
                accept="application/pdf"
                multiple
                hidden
                onChange={(e) => {
                  const files = Array.from(e.target.files || []).slice(0, 5);
                  e.target.value = "";
                  if (files.length) act("upload", () => siRatingApis.uploadPdfReports(rating._id, files, { role: uploadRole }));
                }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={busy === "upload" ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon />}
                onClick={() => fileInput.current?.click()}
                disabled={!!busy}
              >
                Upload PDF
              </Button>
            </div>
          )}
        </div>
        {(rating.pdfReports || []).length === 0 ? (
          <p className="si-rating-muted">
            No reports uploaded. Upload an earlier sustainability report to fill KPIs missing from SDM or to add a
            previous-year baseline for trend analysis.
          </p>
        ) : (
          <ul className="si-rating-files">
            {rating.pdfReports.map((p) => (
              <li key={p._id}>
                <span>{p.name}</span>
                <span className="si-rating-muted">{p.role === "baseline" ? "baseline" : "gap fill"}</span>
                <span className={`si-rating-file-status si-rating-file-status--${p.status}`}>
                  {p.status === "processing" ? "Extracting…" : p.status === "done" ? "Done" : "Failed"}
                </span>
                {p.error && <span className="si-rating-muted">{p.error}</span>}
              </li>
            ))}
          </ul>
        )}

        {pendingCandidates.length > 0 && (
          <table className="si-rating-table">
            <thead>
              <tr>
                <th>Datapoint</th>
                <th>Extracted value</th>
                <th>From</th>
                <th>Use as</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pendingCandidates.map((c) => (
                <tr key={c._id}>
                  <td>
                    <code>{c.datapoint}</code>
                    {c.note && <div className="si-rating-muted">{c.note}</div>}
                  </td>
                  <td>{fmt(c.value)}</td>
                  <td className="si-rating-muted">
                    {c.fileName}
                    {c.metrics?.length ? <div>{c.metrics.join("; ")}</div> : null}
                  </td>
                  <td>{c.role === "baseline" ? "Baseline" : "Gap fill"}</td>
                  <td className="si-rating-row-actions">
                    <Button size="small" disabled={!!busy || finalized} onClick={() => reviewCandidate(c, "accepted")}>
                      Accept
                    </Button>
                    <Button size="small" color="error" disabled={!!busy || finalized} onClick={() => reviewCandidate(c, "rejected")}>
                      Reject
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {reviewedCandidates.length > 0 && (
          <p className="si-rating-muted">
            {reviewedCandidates.filter((c) => c.status === "accepted").length} accepted ·{" "}
            {reviewedCandidates.filter((c) => c.status === "rejected").length} rejected
          </p>
        )}
      </div>

      <div className="si-rating-card">
        <h3>KPI breakdown</h3>
        <Tabs value={sectionTab} onChange={(_, v) => setSectionTab(v)} variant="scrollable" sx={{ minHeight: 36, mb: 1 }}>
          <Tab value="all" label="All" sx={{ minHeight: 36, fontSize: "0.75rem" }} />
          {sections.map((s) => (
            <Tab key={s.key} value={s.key} label={s.label} sx={{ minHeight: 36, fontSize: "0.75rem" }} />
          ))}
        </Tabs>
        <div className="si-rating-table-wrap">
          <table className="si-rating-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>KPI</th>
                <th>Value</th>
                <th>Source</th>
                <th>Benchmark (LB – UB)</th>
                <th>Score</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {kpis.map((k) => {
                const badge = SOURCE_BADGE[k.valueSource];
                const pct = k.normalized != null ? k.normalized * 100 : null;
                return (
                  <tr key={k.code} className={k.status === "scored" && pct < 50 ? "si-row-red" : ""}>
                    <td className="si-rating-muted">{k.code}</td>
                    <td>
                      {k.name}
                      <div className="si-rating-muted">{k.unit}</div>
                    </td>
                    <td>{k.value != null ? fmt(k.value) : "—"}</td>
                    <td>
                      {badge ? (
                        <Tooltip title={k.valueNote || (k.valueFrom || []).join(", ") || ""}>
                          <span className={`si-badge si-badge--${badge.cls}`}>{badge.label}</span>
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="si-rating-muted">{benchmarkText(k)}</td>
                    <td>{pct != null ? fmt(pct, 1) : "—"}</td>
                    <td className="si-rating-muted">{STATUS_LABEL[k.status]}</td>
                    <td>
                      {!finalized && (
                        <Tooltip title="Override value">
                          <Button
                            size="small"
                            sx={{ minWidth: 0 }}
                            onClick={() => {
                              setEditKpi(k);
                              setOverrideValue(k.value != null ? String(k.value) : "");
                              setOverrideNote(k.valueSource === "override" ? k.valueNote || "" : "");
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </Button>
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {(rating.recommendations || []).length > 0 && (
        <div className="si-rating-card">
          <h3>Actionable insights</h3>
          <ul className="si-rating-recs">
            {rating.recommendations.map((r) => (
              <li key={r.code}>
                <span className="si-rating-recs__pts">+{fmt(r.pointsAvailable)} pts</span>
                <div>
                  <b>{r.name}</b> <span className="si-rating-muted">({r.sectionLabel || r.section})</span>
                  <div>{r.action}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={!!editKpi} onClose={() => setEditKpi(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Override {editKpi?.code}</DialogTitle>
        <DialogContent>
          <p className="si-rating-muted" style={{ marginTop: 0 }}>
            {editKpi?.name} ({editKpi?.unit}). Overrides take priority over PDF, SDM and derived values.
          </p>
          <TextField
            fullWidth
            size="small"
            label="Value"
            type="number"
            value={overrideValue}
            onChange={(e) => setOverrideValue(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Reason / source"
            value={overrideNote}
            onChange={(e) => setOverrideNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          {editKpi?.valueSource === "override" && (
            <Button color="error" onClick={() => saveOverride(undefined)} disabled={busy === "override"}>
              Remove override
            </Button>
          )}
          <Button onClick={() => setEditKpi(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => saveOverride(Number(overrideValue))}
            disabled={busy === "override" || overrideValue === "" || !Number.isFinite(Number(overrideValue))}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
